/**
 * One governed event, compactly.
 *
 * Both activity feeds ran the actor, the subject and the timestamp together
 * into one muted sentence separated by middots, so the three facts read as one
 * and nothing said what kind of event it was. Each field is placed rather than
 * concatenated here, and the action carries an icon.
 *
 * @module components/shared/AuditEventRow
 */

import React from "react";

import { auditEventLabel } from "@/components/shared/audit-event-labels";
import { Icon, MutedText, Text, Time } from "@/components/ui";

/**
 * Icons by the part of the system the action names, so a scan down the list
 * separates programme work from account administration without reading.
 */
const SUBJECT_ICONS: Record<string, string | undefined> = {
  programme: "graduation-cap",
  module: "squares-four",
  user: "user",
  users: "user",
  assignment: "users-three",
  tenant: "buildings",
  revision: "git-branch",
  export: "export",
  approval: "seal-check",
};

/** Verbs worth their own mark, checked before the subject. */
const ACTION_ICONS: Record<string, string | undefined> = {
  approved: "seal-check",
  rejected: "seal-warning",
  deleted: "trash",
  created: "plus-circle",
  published: "broadcast",
  withdrawn: "arrow-u-up-left",
};

export function auditEventIcon(action: string): string {
  const parts = action.split(/[.\-_]/).filter(Boolean);
  const verb = parts[parts.length - 1]?.toLocaleLowerCase() ?? "";
  const subject = parts[0]?.toLocaleLowerCase() ?? "";
  return ACTION_ICONS[verb] ?? SUBJECT_ICONS[subject] ?? "clock-counter-clockwise";
}

export interface AuditEventRowProps {
  action: string;
  /** Who did it. */
  actor: string;
  /** What it was done to: a programme, a user, a tenant. */
  subject?: string;
  timestamp: string;
  /** Present makes the whole row the control, with no separate button. */
  onOpen?: () => void;
  /** Names what opening it reaches, since the row's own text is the event. */
  openLabel?: string;
  "data-testid"?: string;
}

/**
 * @example
 * ```tsx
 * <AuditEventRow action={event.action} actor={name} subject={title}
 *   timestamp={event.timestamp} onOpen={() => open(id)} openLabel={title} />
 * ```
 */
export function AuditEventRow({
  action,
  actor,
  subject = undefined,
  timestamp,
  onOpen = undefined,
  openLabel = undefined,
  "data-testid": testId,
}: AuditEventRowProps): React.ReactElement {
  const body = (
    <>
      <Icon
        name={auditEventIcon(action)}
        size="lg"
        className="col-start-1 row-start-1 mt-0.5 shrink-0 text-muted"
        aria-hidden
      />
      <span className="col-start-2 row-start-1 min-w-0 sm:self-baseline">
        <Text as="span" className="block truncate font-medium">
          {auditEventLabel(action)}
        </Text>
        <MutedText size="caption" as="span" className="mt-0.5 block truncate">
          {actor}
          {subject ? <span className="text-muted-light"> · </span> : null}
          {subject}
        </MutedText>
      </span>
      {/* Under the event on a narrow row, back on the line at `sm`. Left as a
          wrapped flex item it took `ml-auto` with it and sat against the right
          edge on a line of its own, beside nothing. */}
      <MutedText
        size="caption"
        as="span"
        className="col-start-2 row-start-2 shrink-0 tabular-nums sm:col-start-3 sm:row-start-1 sm:self-baseline"
      >
        <Time value={timestamp} variant="relative" />
      </MutedText>
      {onOpen ? (
        // The affordance, rather than a labelled button: the row is the
        // control, so a second one inside it would be a second tab stop for
        // the same action. Pinned to the first row's last column, so it is in
        // the corner at every width rather than trailing the timestamp.
        <Icon
          name="arrow-square-out"
          className="col-start-3 row-start-1 mt-0.5 shrink-0 text-muted sm:col-start-4"
          aria-hidden
        />
      ) : null}
    </>
  );

  const shell =
    "grid w-full grid-cols-[auto_1fr_auto] items-start gap-x-3 gap-y-1 rounded-box px-3 py-2 text-left sm:grid-cols-[auto_1fr_auto_auto]";

  return onOpen ? (
    <button
      type="button"
      onClick={onOpen}
      className={`${shell} hover:bg-surface-hover`}
      aria-label={openLabel ? `${auditEventLabel(action)} — open ${openLabel}` : undefined}
      data-testid={testId}
    >
      {body}
    </button>
  ) : (
    <div className={shell} data-testid={testId}>
      {body}
    </div>
  );
}
